import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { toast } from "react-toastify";
import {
  addNewLead,
  sendOtpToLeadMobile,
  verifyOtpToLeadMobile,
  sendOtpToLeadEmail,
  verifyOtpToLeadEmail,
  allProductUrl,
  commonUrl,
  getCategoriesWithSubCategoriesUrl,
} from "../../utils/urls";
import { useNavigate } from "react-router-dom";

// Validation schemas for each step
const detailsValidationSchema = Yup.object().shape({
  name: Yup.string()
    .matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed")
    .min(2, "Minimum 2 characters")
    .max(100, "Maximum 100 characters")
    .required("Name is required"),
  mobileNo: Yup.string()
    .matches(/^[0-9]{10}$/, "Must be exactly 10 digits")
    .required("Mobile number is required"),
  mobileOtp: Yup.string().when("mobileOtpSent", {
    is: true,
    then: Yup.string().required("OTP is required"),
  }),
  email: Yup.string()
    .email("Invalid email format")
    .matches(
      /^[^\s@]{2,}@[^\s@]+\.[^\s@]+$/,
      "Email must have at least 2 characters before @"
    )
    .required("Email is required"),
  emailOtp: Yup.string().when("emailOtpSent", {
    is: true,
    then: Yup.string().required("OTP is required"),
  }),
  dob: Yup.string().required("Date of birth is required"),
  address: Yup.string()
    .min(5, "Minimum 5 characters")
    .max(255, "Maximum 255 characters")
    .required("Address is required"),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
});

const categoryValidationSchema = Yup.object().shape({
  category: Yup.string().required("Please select a category"),
});

const productsValidationSchema = Yup.object().shape({
  products: Yup.string().required("Please select a product"),
});

const subCategoryValidationSchema = Yup.object().shape({
  subCategory: Yup.string().required("Please select a subcategory"),
});

const reviewValidationSchema = Yup.object().shape({
  priority: Yup.string().required("Priority is required"),
  notes: Yup.string().optional(),
});

const AddLeadPopup = ({
  isOpen,
  onClose,
  onLeadAdded,
  preselectedProduct = null,
  preselectedSubCategory = null,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(
    preselectedProduct?.category_title || ""
  );
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedProductId, setSelectedProductId] = useState(
    preselectedProduct?.product_id || null
  );
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(
    preselectedProduct
      ? {
          id: preselectedProduct.product_id,
          planName: preselectedProduct.product_name,
          company: preselectedProduct.company_name,
          productType: preselectedProduct.sub_category_title || "N/A",
          coverage: preselectedProduct.description || "N/A",
          commission_percentage: preselectedProduct.commission_percentage ?? null,
        }
      : null
  );
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [mobileOtpSent, setMobileOtpSent] = useState(false);
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [isMobileVerified, setIsMobileVerified] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [mobileToken, setMobileToken] = useState("");
  const [emailToken, setEmailToken] = useState("");
  const [products, setProducts] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [categories, setCategories] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [error, setError] = useState(null);
  const formikRef = useRef(null);
  const observerRef = useRef(null);
  const lastRowRef = useRef(null);
  const limit = 10;
  const navigate = useNavigate();
  const [isLeadAddWithoutProduct, setIsLeadAddWithoutProduct] = useState(false);
  const [hasSubCategories, setHasSubCategories] = useState(false);

  // Determine steps based on preselectedProduct and is_lead_add_without_product
  const getSteps = () => {
    if (preselectedProduct || preselectedSubCategory) {
      return ["Details", "Review"];
    } else {
      return ["Details", "Category", "Products", "Review"];
    }
  };

  const [steps, setSteps] = useState(getSteps());

  // Fetch states and cities
  useEffect(() => {
    const fetchStatesAndCities = async () => {
      try {
        const response = await axios.get(`${commonUrl}/get-state-with-city`);
        if (response.data.success) {
          const activeStates = response.data.data
            .filter((state) => state.is_active && !state.is_delete)
            .map((state) => ({
              id: state.id,
              name: state.title.trim(),
              cities: state.city
                .filter((city) => city.is_active && !city.is_delete)
                .map((city) => ({
                  id: city.id,
                  name: city.title.trim(),
                  state_id: city.state_id,
                })),
            }));
          setStates(activeStates);
          setCities(activeStates[0]?.cities || []);
          setError(null);
        } else {
          setError("Failed to fetch states and cities");
        }
      } catch (e) {
        console.error("Error fetching states and cities:", e);
        setError("Error fetching states and cities. Please try again.");
      }
    };
    fetchStatesAndCities();
  }, []);

  // Fetch categories (only if no preselected product)
  useEffect(() => {
    if (preselectedProduct) return;
    const fetchCategories = async () => {
      try {
        const response = await axios.get(
          getCategoriesWithSubCategoriesUrl + "?is_all=true"
        );
        if (response.data.success) {
          const activeCategories = response.data.data.categories
            .filter((category) => category.is_active && !category.is_delete)
            .map((category) => ({
              id: category.category_id,
              title: category.title,
              logo_url: category.logo_url,
              is_lead_add_without_product: category.is_lead_add_without_product,
              sub_categories: category.sub_category
                .filter((sub) => sub.is_active && !sub.is_delete)
                .map((sub) => ({
                  id: sub.sub_category_id,
                  name: sub.title,
                  description: sub.description || "N/A",
                  logo_url: sub.logo_url,
                })),
            }));
          setCategories(activeCategories);
          setError(null);
        } else {
          setError("Failed to fetch categories");
        }
      } catch (e) {
        console.error("Error fetching categories:", e);
        setError("Error fetching categories. Please try again.");
      }
    };
    fetchCategories();
  }, [preselectedProduct]);

  // Reset states when category changes
  useEffect(() => {
    if (preselectedProduct) return;
    const selectedCat = categories.find((cat) => cat.id === selectedCategoryId);
    if (selectedCat) {
      setIsLeadAddWithoutProduct(selectedCat.is_lead_add_without_product);
      setHasSubCategories(selectedCat.sub_categories.length > 0);
      setSubCategories(selectedCat.sub_categories);
      setSteps(getSteps());
      setProducts([]);
      setPage(1);
      setHasMore(true);
      setSelectedProductId(null);
      setSelectedProduct(null);
      setSelectedSubCategoryId(null);
      setSelectedSubCategory(null);
      if (formikRef.current) {
        formikRef.current.setFieldValue("products", "");
        formikRef.current.setFieldValue("subCategory", "");
      }
    }
  }, [selectedCategoryId, preselectedProduct, categories]);

  // Automatically skip to Review step if is_lead_add_without_product is true and no subcategories
  useEffect(() => {
    if (
      currentStep === 3 &&
      isLeadAddWithoutProduct &&
      !hasSubCategories &&
      !preselectedProduct
    ) {
      setCurrentStep(4);
    }
  }, [
    currentStep,
    isLeadAddWithoutProduct,
    hasSubCategories,
    preselectedProduct,
  ]);

  // Fetch products for step 3 with infinite scroll (only if no preselected product and is_lead_add_without_product is false)
  const fetchProducts = async (pageNum) => {
    if (
      preselectedProduct ||
      isLeadAddWithoutProduct ||
      !selectedCategoryId ||
      !hasMore ||
      isLoadingProducts
    )
      return;
    setIsLoadingProducts(true);
    try {
      const response = await axios.get(allProductUrl, {
        params: {
          category_id: selectedCategoryId,
          page: pageNum,
          per_page: limit,
        },
      });
      if (response.data.success) {
        const newProducts = response.data.data.map((item) => ({
          id: item.product_id,
          name: item.product_name,
          company: item.company_name,
          title: item.sub_category_title || "N/A",
          description: item.description || "N/A",
          commission_percentage: item.commission_percentage ?? null,
        }));
        setProducts((prev) =>
          pageNum === 1 ? newProducts : [...prev, ...newProducts]
        );
        setHasMore(newProducts.length === limit);
      } else {
        setError("Failed to fetch products");
        setHasMore(false);
      }
    } catch (e) {
      console.error("Error fetching products:", e);
      setError("Error fetching products. Please try again.");
      setHasMore(false);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // Fetch products when step 3 is active
  useEffect(() => {
    if (
      preselectedProduct ||
      currentStep !== 3 ||
      isLeadAddWithoutProduct ||
      !selectedCategoryId ||
      page !== 1
    )
      return;
    fetchProducts(1);
  }, [
    currentStep,
    selectedCategoryId,
    preselectedProduct,
    isLeadAddWithoutProduct,
  ]);

  // Set up infinite scroll with IntersectionObserver (only for products)
  useEffect(() => {
    if (
      preselectedProduct ||
      currentStep !== 3 ||
      isLeadAddWithoutProduct ||
      !hasMore ||
      isLoadingProducts
    )
      return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (lastRowRef.current) {
      observerRef.current.observe(lastRowRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    currentStep,
    hasMore,
    isLoadingProducts,
    preselectedProduct,
    isLeadAddWithoutProduct,
  ]);

  // Fetch more products when page changes
  useEffect(() => {
    if (
      preselectedProduct ||
      currentStep !== 3 ||
      isLeadAddWithoutProduct ||
      page <= 1
    )
      return;
    fetchProducts(page);
  }, [page, preselectedProduct, isLeadAddWithoutProduct]);

  // Update cities when state changes
  const handleStateChange = (stateId, setFieldValue) => {
    const selectedState = states.find((state) => state.id === stateId);
    setCities(selectedState ? selectedState.cities : []);
    setFieldValue("city", "");
  };

  // Handle sending OTP
  const handleSendOtp = async (type, value) => {
    try {
      if (type === "mobile") {
        const response = await axios.post(sendOtpToLeadMobile, {
          mobile_number: value,
        });
        if (response.data.success) {
          setMobileToken(response.data.data.token);
          setMobileOtpSent(true);
          setError(null);
        } else {
          setError("Failed to send mobile OTP");
        }
      } else if (type === "email") {
        const response = await axios.post(sendOtpToLeadEmail, { email: value });
        if (response.data.success) {
          setEmailToken(response.data.data.token);
          setEmailOtpSent(true);
          setError(null);
        } else {
          setError("Failed to send email OTP");
        }
      }
    } catch (e) {
      console.error(`Error sending ${type} OTP:`, e);
      setError(`Failed to send ${type} OTP`);
    }
  };

  // Handle verifying OTP
  const handleVerifyOtp = async (type, values) => {
    try {
      if (type === "mobile") {
        const response = await axios.post(verifyOtpToLeadMobile, {
          token: mobileToken,
          otp: Number(values.mobileOtp),
          mobile_number: values.mobileNo,
        });
        if (response.data.success) {
          setIsMobileVerified(true);
          setError(null);
          toast.success("Mobile number verified successfully!");
        } else {
          setError("Invalid mobile OTP");
        }
      } else if (type === "email") {
        const response = await axios.post(verifyOtpToLeadEmail, {
          token: emailToken,
          otp: values.emailOtp,
          email: values.email,
        });
        if (response.data.success) {
          setIsEmailVerified(true);
          setError(null);
          toast.success("Email verified successfully!");
        } else {
          setError("Invalid email OTP");
        }
      }
    } catch (e) {
      console.error(`Error verifying ${type} OTP:`, e);
      setError(`Invalid ${type} OTP`);
    }
  };

  // Handle form submission for each step
  const handleStepSubmit = async (values, { resetForm }) => {
    setFormData({ ...formData, ...values });
    console.log("currentStep", currentStep);
    console.log(
      "isLeadAddWithoutProduct && !hasSubCategories",
      isLeadAddWithoutProduct && !hasSubCategories
    );

    const isLastStep = currentStep === 4;

    if (currentStep === 1) {
      if (!isMobileVerified || !isEmailVerified) {
        setError(
          "Please verify both mobile number and email before proceeding."
        );
        return;
      }
      setCurrentStep((preselectedProduct || preselectedSubCategory) ? 2 : Math.min(currentStep + 1, 4));
      setError(null);
    } else if (!preselectedProduct && currentStep === 2) {
      const selectedCat = categories.find(
        (cat) => cat.title === values.category
      );
      setSelectedCategory(values.category);
      setSelectedCategoryId(selectedCat?.id || "");
      setIsLeadAddWithoutProduct(
        selectedCat?.is_lead_add_without_product || false
      );
      setHasSubCategories(selectedCat?.sub_categories?.length > 0 || false);
      setSubCategories(selectedCat?.sub_categories || []);
      setProducts([]);
      setSelectedProductId(null);
      setSelectedProduct(null);
      setSelectedSubCategoryId(null);
      setSelectedSubCategory(null);
      if (formikRef.current) {
        formikRef.current.setFieldValue("products", "");
        formikRef.current.setFieldValue("subCategory", "");
      }
      setSteps(getSteps());
      if (
        selectedCat?.is_lead_add_without_product &&
        !selectedCat?.sub_categories?.length
      ) {
        console.log("skipping to step");

        setCurrentStep(4); // Skip to Review
      } else {
        setCurrentStep((prev) => Math.min(prev + 1, 4));
      }
      setError(null);
    } else if (!preselectedProduct && currentStep === 3) {
      setCurrentStep((prev) => Math.min(prev + 1, 4));

      setError(null);
    } else if (isLastStep) {
      try {
        const payload = {
          name: formData.name,
          email: formData.email,
          contact_number: formData.mobileNo,
          dob: formData.dob,
          address: formData.address,
          city_id: cities.find((city) => city.name === formData.city)?.id || "",
          priority: values.priority,
          additinal_note: values.notes,
        };
        if (preselectedProduct || selectedProductId) {
          payload.product_id =
            preselectedProduct?.product_id || selectedProductId || "";
        } else {
          payload.category_id = selectedCategoryId || "";
          if (isLeadAddWithoutProduct && hasSubCategories) {
            payload.sub_category_id = selectedSubCategoryId || "";
          }
        }
        const response = await axios.post(addNewLead, payload);
        if (response.data.success) {
          setCurrentStep(1);
          setSelectedCategory(preselectedProduct?.category_title || "");
          setSelectedCategoryId("");
          setSelectedProductId(preselectedProduct?.product_id || null);
          setSelectedSubCategoryId(null);
          setSelectedProduct(
            preselectedProduct
              ? {
                  id: preselectedProduct.product_id,
                  name: preselectedProduct.product_name,
                  company: preselectedProduct.company_name,
                  title: preselectedProduct.sub_category_title || "N/A",
                  description: preselectedProduct.description || "N/A",
                  commission_percentage: preselectedProduct.commission_percentage ?? null,
                }
              : null
          );
          setSelectedSubCategory(null);
          setMobileOtpSent(false);
          setEmailOtpSent(false);
          setIsMobileVerified(false);
          setIsEmailVerified(false);
          setProducts([]);
          setSubCategories([]);
          setPage(1);
          setHasMore(true);
          setIsLeadAddWithoutProduct(false);
          setHasSubCategories(false);
          setSteps(getSteps());
          resetForm();
          setError(null);
          onClose();
          onLeadAdded();
          console.log("452", response?.data?.data);

          navigate(`/leads/details/${response?.data?.data?.lpr_id}`);
          toast.success("Lead added successfully!");
        } else {
          setError("Failed to add new lead");
        }
      } catch (e) {
        console.error("Error adding new lead:", e);
        setError("Failed to add new lead");
      }
    } else {
      setCurrentStep((prev) => Math.min(prev + 1, 4));

      setError(null);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (currentStep > 1) {
      if (
        (currentStep === 4 || currentStep === 3) &&
        isLeadAddWithoutProduct &&
        !hasSubCategories
      ) {
        setCurrentStep(2); // Skip back to Category
      } else {
        setCurrentStep(currentStep - 1);
      }
      setError(null);
    }
  };

  // Handle reset
  const handleReset = () => {
    if (formikRef.current) {
      formikRef.current.resetForm();
      setSelectedCategory(preselectedProduct?.category_title || "");
      setSelectedCategoryId("");
      setSelectedProductId(preselectedProduct?.product_id || null);
      setSelectedSubCategoryId(null);
      setSelectedProduct(
        preselectedProduct
          ? {
              id: preselectedProduct.product_id,
              name: preselectedProduct.product_name,
              company: preselectedProduct.company_name,
              title: preselectedProduct.sub_category_title || "N/A",
              description: preselectedProduct.description || "N/A",
              commission_percentage: preselectedProduct.commission_percentage ?? null,
            }
          : null
      );
      setSelectedSubCategory(null);
      setMobileOtpSent(false);
      setEmailOtpSent(false);
      setIsMobileVerified(false);
      setIsEmailVerified(false);
      setProducts([]);
      setSubCategories([]);
      setPage(1);
      setHasMore(true);
      setIsLeadAddWithoutProduct(false);
      setHasSubCategories(false);
      setSteps(getSteps());
      if (currentStep === 3 && !preselectedProduct) {
        formikRef.current.setFieldValue("products", "");
        formikRef.current.setFieldValue("subCategory", "");
      }
      setError(null);
    }
  };

  const getFormIdForStep = () => {
    console.log("currentStep", currentStep);

    if (preselectedProduct || preselectedSubCategory) {
      return currentStep === 1 ? "1" : "2";
    }
    if (currentStep === 4) return "4";
    if (currentStep === 3) return "3";
    if (currentStep === 2) return "2";
    return "1";
  };

  const renderStepContent = () => {
    const stepIndex = preselectedProduct
      ? currentStep === 1
        ? 1
        : 4
      : currentStep;
    switch (stepIndex) {
      case 1:
        return (
          <Formik
            innerRef={formikRef}
            initialValues={{
              name: formData.name || "",
              mobileNo: formData.mobileNo || "",
              mobileOtp: "",
              email: formData.email || "",
              emailOtp: "",
              dob: formData.dob || "",
              address: formData.address || "",
              city: formData.city || "",
              state: formData.state || "",
            }}
            validationSchema={detailsValidationSchema}
            onSubmit={handleStepSubmit}
          >
            {({ errors, touched, values, setFieldValue }) => (
              <Form id="1" className="p-6">
                {error && (
                  <div className="text-red-500 text-sm mb-4">{error}</div>
                )}
                <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <Field
                      name="name"
                      className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter name"
                    />
                    {errors.name && touched.name && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.name}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mobile No
                    </label>
                    <div className="flex gap-2">
                      <Field
                        name="mobileNo"
                        className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter mobile number"
                        disabled={isMobileVerified}
                      />
                      {!isMobileVerified && (
                        <button
                          type="button"
                          onClick={() =>
                            handleSendOtp("mobile", values.mobileNo)
                          }
                          className="py-2 px-4 bg-white text-blue-600 rounded-lg text-sm font-medium hover:bg-gray-100"
                          disabled={!values.mobileNo || errors.mobileNo}
                        >
                          {mobileOtpSent ? "Resend OTP" : "Verify"}
                        </button>
                      )}
                    </div>
                    {errors.mobileNo && touched.mobileNo && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.mobileNo}
                      </div>
                    )}
                    {mobileOtpSent && !isMobileVerified && (
                      <div className="mt-2">
                        <Field
                          name="mobileOtp"
                          className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter OTP"
                        />
                        <button
                          type="button"
                          onClick={() => handleVerifyOtp("mobile", values)}
                          className="mt-2 py-2 px-4 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                        >
                          Verify OTP
                        </button>
                        {errors.mobileOtp && touched.mobileOtp && (
                          <div className="text-red-500 text-xs mt-1">
                            {errors.mobileOtp}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <div className="flex gap-2">
                      <Field
                        name="email"
                        className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter email"
                        disabled={isEmailVerified}
                      />
                      {!isEmailVerified && (
                        <button
                          type="button"
                          onClick={() => handleSendOtp("email", values.email)}
                          className="py-2 px-4 bg-white text-blue-600 rounded-lg text-sm font-medium hover:bg-gray-100"
                          disabled={!values.email || errors.email}
                        >
                          {emailOtpSent ? "Resend OTP" : "Verify"}
                        </button>
                      )}
                    </div>
                    {errors.email && touched.email && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.email}
                      </div>
                    )}
                    {emailOtpSent && !isEmailVerified && (
                      <div className="mt-2">
                        <Field
                          name="emailOtp"
                          className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter OTP"
                        />
                        <button
                          type="button"
                          onClick={() => handleVerifyOtp("email", values)}
                          className="mt-2 py-2 px-4 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                        >
                          Verify OTP
                        </button>
                        {errors.emailOtp && touched.emailOtp && (
                          <div className="text-red-500 text-xs mt-1">
                            {errors.emailOtp}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date of Birth
                    </label>
                    <Field
                      name="dob"
                      type="date"
                      className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.dob && touched.dob && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.dob}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State
                    </label>
                    <Field
                      as="select"
                      name="state"
                      className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onChange={(e) => {
                        setFieldValue("state", e.target.value);
                        handleStateChange(
                          states.find((s) => s.name === e.target.value)?.id,
                          setFieldValue
                        );
                      }}
                    >
                      <option value="">Select state</option>
                      {states.map((state) => (
                        <option key={state.id} value={state.name}>
                          {state.name}
                        </option>
                      ))}
                    </Field>
                    {errors.state && touched.state && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.state}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <Field
                      as="select"
                      name="city"
                      className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select city</option>
                      {cities.map((city) => (
                        <option key={city.id} value={city.name}>
                          {city.name}
                        </option>
                      ))}
                    </Field>
                    {errors.city && touched.city && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.city}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <Field
                      name="address"
                      className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter address"
                    />
                    {errors.address && touched.address && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.address}
                      </div>
                    )}
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        );
      case 2:
        if (preselectedProduct) return null;
        return (
          <Formik
            innerRef={formikRef}
            initialValues={{ category: selectedCategory }}
            validationSchema={categoryValidationSchema}
            onSubmit={handleStepSubmit}
          >
            {({ errors, touched, values }) => (
              <Form id="2" className="p-6">
                {error && (
                  <div className="text-red-500 text-sm mb-4">{error}</div>
                )}
                <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-6">
                  {categories.map((category) => (
                    <label
                      key={category.id}
                      className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-colors duration-200 ${
                        // selectedCategory === category.title
                        values?.category === category.title
                          ? "border-blue-600 bg-blue-50"
                          : "border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={category.logo_url}
                          alt={`${category.title} logo`}
                          className="w-10 h-10 object-contain"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          {category.title}
                        </span>
                      </div>
                      <Field
                        type="radio"
                        name="category"
                        value={category.title}
                        className="form-radio text-blue-600 focus:ring-blue-500"
                      />
                    </label>
                  ))}
                </div>
                {errors.category && touched.category && (
                  <div className="text-red-500 text-xs mt-2">
                    {errors.category}
                  </div>
                )}
              </Form>
            )}
          </Formik>
        );
      case 3:
        if (preselectedProduct) return null;
        if (isLeadAddWithoutProduct && hasSubCategories) {
          return (
            <Formik
              innerRef={formikRef}
              initialValues={{ subCategory: selectedSubCategoryId || "" }}
              validationSchema={subCategoryValidationSchema}
              onSubmit={handleStepSubmit}
            >
              {({ errors, touched, values }) => (
                <Form id="3" className="p-6">
                  {error && (
                    <div className="text-red-500 text-sm mb-4">{error}</div>
                  )}
                  <div className="bg-white">
                    <div style={{ maxHeight: "40rem", overflowY: "auto" }}>
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50 sticky top-0">
                            <th className="text-left p-4 font-medium text-sm text-gray-700">
                              Select
                            </th>
                            <th className="text-left p-4 font-medium text-sm text-gray-700 max-sm:w-[13rem]">
                              Subcategory Name
                            </th>
                            <th className="text-left p-4 font-medium text-sm text-gray-700  max-sm:w-[26rem]">
                              Description
                            </th>
                            {/* <th className="text-left p-4 font-medium text-sm text-gray-700"></th>
                            <th className="text-left p-4 font-medium text-sm text-gray-700"></th> */}
                          </tr>
                        </thead>
                        <tbody>
                          {subCategories.map((subCategory, index) => (
                            <tr
                              key={subCategory.id}
                              className={`border-b border-gray-200 cursor-pointer hover:bg-gray-50 ${
                                selectedSubCategoryId === subCategory.id
                                  ? "bg-blue-50"
                                  : ""
                              }`}
                              onClick={() => {
                                setSelectedSubCategoryId(subCategory.id);
                                setSelectedSubCategory(subCategory);
                                if (formikRef.current) {
                                  formikRef.current.setFieldValue(
                                    "subCategory",
                                    subCategory.id
                                  );
                                }
                              }}
                            >
                              <td className="p-4">
                                <Field
                                  type="radio"
                                  name="subCategory"
                                  value={subCategory.id}
                                  checked={
                                    selectedSubCategoryId === subCategory.id
                                  }
                                  onChange={() => {
                                    setSelectedSubCategoryId(subCategory.id);
                                    setSelectedSubCategory(subCategory);
                                    if (formikRef.current) {
                                      formikRef.current.setFieldValue(
                                        "subCategory",
                                        subCategory.id
                                      );
                                    }
                                  }}
                                  className="text-blue-600 focus:ring-blue-500"
                                  style={{ margin: "0 8px", cursor: "pointer" }}
                                />
                              </td>
                              <td className="p-4 text-sm text-gray-600">
                                {subCategory.name}
                              </td>
                              <td className="p-4 text-sm text-gray-600">
                                {subCategory.description}
                              </td>
                              {/* <td className="p-4"></td>
                              <td className="p-4"></td> */}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {subCategories.length === 0 && (
                        <div className="p-4 text-center text-sm text-gray-600">
                          No subcategories found.
                        </div>
                      )}
                    </div>
                  </div>
                  {errors.subCategory && touched.subCategory && (
                    <div className="text-red-500 text-xs mt-2">
                      {errors.subCategory}
                    </div>
                  )}
                </Form>
              )}
            </Formik>
          );
        } else {
          return (
            <Formik
              innerRef={formikRef}
              initialValues={{ products: selectedProductId || "" }}
              validationSchema={productsValidationSchema}
              onSubmit={handleStepSubmit}
            >
              {({ errors, touched, values }) => (
                <Form id="3" className="p-6">
                  {error && (
                    <div className="text-red-500 text-sm mb-4">{error}</div>
                  )}
                  <div className="bg-white">
                    <div style={{ maxHeight: "40rem", overflowY: "auto" }}>
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50 sticky top-0">
                            <th className="text-left p-4 font-medium text-sm text-gray-700">
                              Select
                            </th>
                            <th className="text-left p-4 font-medium text-sm text-gray-700">
                              <p className="max-sm:w-[13rem]">Plan Name</p>
                            </th>
                            <th className="text-left p-4 font-medium text-sm text-gray-700">
                              <p className="max-sm:w-[13rem]">Company</p>
                            </th>
                            <th className="text-left p-4 font-medium text-sm text-gray-700">
                              <p className="max-sm:w-[13rem]">Product Type</p>
                            </th>
                            <th className="text-left p-4 font-medium text-sm text-gray-700">
                              <p className="max-sm:w-[26rem]">Description</p>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {products.map((product, index) => {
                            const isLastRow = index === products.length - 1;
                            return (
                              <tr
                                key={product.id}
                                ref={isLastRow ? lastRowRef : null}
                                className={`border-b border-gray-200 cursor-pointer hover:bg-blue-50 ${
                                  selectedProductId === product.id
                                    ? "bg-blue-50"
                                    : ""
                                }`}
                                onClick={() => {
                                  setSelectedProductId(product.id);
                                  setSelectedProduct(product);
                                  if (formikRef.current) {
                                    formikRef.current.setFieldValue(
                                      "products",
                                      product.id
                                    );
                                  }
                                }}
                              >
                                <td className="p-4">
                                  <Field
                                    type="radio"
                                    name="products"
                                    value={product.id}
                                    checked={selectedProductId === product.id}
                                    onChange={() => {
                                      setSelectedProductId(product.id);
                                      setSelectedProduct(product);
                                      if (formikRef.current) {
                                        formikRef.current.setFieldValue(
                                          "products",
                                          product.id
                                        );
                                      }
                                    }}
                                    className="text-blue-600 focus:ring-blue-500"
                                    style={{
                                      margin: "0 8px",
                                      cursor: "pointer",
                                    }}
                                  />
                                </td>
                                <td className="p-4 text-sm text-gray-600">
                                  {product.name}
                                </td>
                                <td className="p-4 text-sm text-gray-600">
                                  {product.company}
                                </td>
                                <td className="p-4 text-sm text-gray-600">
                                  {product.title}
                                </td>
                                <td className="p-4 text-sm text-gray-600">
                                  {product.description}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {isLoadingProducts && (
                        <div className="p-4 text-center text-sm text-gray-600">
                          Loading more products...
                        </div>
                      )}
                      {products.length === 0 && !isLoadingProducts && (
                        <div className="p-4 text-center text-sm text-gray-600">
                          No products found. Please go back to choose a
                          different category.
                        </div>
                      )}
                    </div>
                  </div>
                  {errors.products && touched.products && (
                    <div className="text-red-500 text-xs mt-2">
                      {errors.products}
                    </div>
                  )}
                </Form>
              )}
            </Formik>
          );
        }
      case 4:
        return (
          <Formik
            innerRef={formikRef}
            initialValues={{
              priority: formData.priority || "High",
              notes: formData.notes || "",
              products: selectedProductId || "",
              subCategory: selectedSubCategoryId || "",
            }}
            validationSchema={reviewValidationSchema}
            onSubmit={handleStepSubmit}
          >
            {({ errors, touched }) => (
              <Form id={preselectedProduct ? "2" : "4"} className="p-6">
                {error && (
                  <div className="text-red-500 text-sm mb-4">{error}</div>
                )}
                <h3 className="text-lg font-semibold text-gray-800 mb-6">
                  Lead Review
                </h3>
                <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-6 mb-6">
                  <div>
                    <h4 className="font-extrabold text-sm text-gray-700 mb-3">
                      Lead Details
                    </h4>
                    <div className="space-y-2 text-sm text-gray-600">
                      <p>
                        <span className="font-medium">Email:</span>{" "}
                        {formData.email}
                      </p>
                      <p>
                        <span className="font-medium">Mobile Number:</span>{" "}
                        {formData.mobileNo}
                      </p>
                      <p>
                        <span className="font-medium">Date of Birth:</span>{" "}
                        {formData.dob}
                      </p>
                      <p>
                        <span className="font-medium">State:</span>{" "}
                        {formData.state}
                      </p>
                      <p>
                        <span className="font-medium">City:</span>{" "}
                        {formData.city}
                      </p>
                      <p>
                        <span className="font-medium">Address:</span>{" "}
                        {formData.address}
                      </p>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-extrabold text-sm text-gray-700 mb-3">
                      Plan Details
                    </h4>
                    <div className="space-y-2 text-sm text-gray-600">
                      <p>
                        <span className="font-medium">Category:</span>{" "}
                        {selectedCategory}
                      </p>
                      {isLeadAddWithoutProduct && hasSubCategories ? (
                        <p>
                          <span className="font-medium">
                            Subcategory Selected:
                          </span>{" "}
                          {selectedSubCategory
                            ? selectedSubCategory.name
                            : "None"}
                        </p>
                      ) : (
                        <p>
                          <span className="font-medium">Product Selected:</span>{" "}
                          {selectedProduct
                            ? selectedProduct.name || selectedProduct.planName
                            : "None"}
                        </p>
                      )}
                      <p>
                        <span className="font-medium">
                          Coverage/Description:
                        </span>{" "}
                        {selectedSubCategory
                          ? selectedSubCategory.description
                          : selectedProduct
                          ? selectedProduct.description ||
                            selectedProduct.coverage
                          : "N/A"}
                      </p>
                      {selectedProduct?.commission_percentage != null && (
                        <div className="mt-3 flex items-center gap-2 bg-[#EAF4FF] border border-[#B8D9FF] rounded-lg px-3 py-2">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#0184FF" className="w-4 h-4 flex-shrink-0"><path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-11.25a.75.75 0 0 0-1.5 0v2.5h-2.5a.75.75 0 0 0 0 1.5h2.5v2.5a.75.75 0 0 0 1.5 0v-2.5h2.5a.75.75 0 0 0 0-1.5h-2.5v-2.5Z" clipRule="evenodd" /></svg>
                          <span className="text-[0.8125rem] text-[#0F172A]">
                            <span className="font-semibold text-[#0184FF]">{selectedProduct.commission_percentage}%</span>
                            {" "}commission on this product
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Priority
                    </label>
                    <Field
                      as="select"
                      name="priority"
                      className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select priority</option>
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </Field>
                    {errors.priority && touched.priority && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.priority}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Notes
                    </label>
                    <Field
                      as="textarea"
                      name="notes"
                      className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 h-24 resize-none"
                      placeholder="Enter notes"
                    />
                    {errors.notes && touched.notes && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.notes}
                      </div>
                    )}
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        );
      default:
        console.warn("Unhandled step:", currentStep);
        return null;
    }
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-xl shadow-lg w-[min(56rem_,_90%)] h-[600px] flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                Add New Lead
              </h3>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            {/* Stepper */}
            <div className="border-b border-gray-200">
              <div
                className="flex items-center justify-between p-6"
                style={(steps?.length === 2) ? {width: "min(30rem, 100%)", margin: "0 auto"} : {}}
              >
                {steps.map((step, index) => {
                  const stepNumber = index + 1;
                  const isActiveOrCompleted = stepNumber <= currentStep;
                  const isLastStep = index === steps.length - 1;
                  return (
                    <div
                      key={step}
                      className={`flex items-center ${
                        isLastStep ? "" : "flex-1"
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                            isActiveOrCompleted
                              ? "bg-blue-600 text-white"
                              : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          {stepNumber}
                        </div>
                        <span
                          className={`mt-2 text-xs font-medium ${
                            isActiveOrCompleted
                              ? "text-blue-600"
                              : "text-gray-600"
                          }`}
                        >
                          {step}
                        </span>
                      </div>
                      {index < steps.length - 1 && (
                        <div
                          className={`flex-1 h-1 mx-2 ${
                            stepNumber < currentStep
                              ? "bg-blue-600"
                              : "bg-gray-200"
                          }`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Form Content */}
            <div className="flex-1 overflow-y-auto">{renderStepContent()}</div>
            {/* Footer */}
            <div className="flex justify-between p-6 border-t border-gray-200">
              <button
                onClick={handleBack}
                disabled={currentStep === 1}
                className="py-2 px-4 bg-white text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-100 disabled:opacity-50"
              >
                Back
              </button>
              <div className="flex gap-2">
                <button
                  onClick={handleReset}
                  className="py-2 px-4 bg-white text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-100"
                >
                  Reset
                </button>

                <button
                  type="submit"
                  form={getFormIdForStep()}
                  className="py-2 px-4 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  {(() => {
                    if (preselectedProduct) {
                      return currentStep === 1 ? "Next" : "Add Lead";
                    }

                    const isSkippedFlow =
                      isLeadAddWithoutProduct && !hasSubCategories;

                    if (
                      currentStep === 4 &&
                      (isSkippedFlow || !isSkippedFlow)
                    ) {
                      return "Add Lead";
                    }

                    return "Next";
                  })()}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddLeadPopup;
